/**
 * Checks for the dashboard "Get Directions" flows (InspectionsDue, OpenFindings).
 *
 * SW2-2468. Project coordinates are free text (`addresses.latitude/longitude`), and
 * `isValidLatLong` only range-checks them, so placeholders such as "0" / "-11" reached
 * the route optimiser, which cannot drive to the middle of the Atlantic and failed with
 * a generic error that did not say which project was at fault. Prod data also holds
 * "39.96141 N", "-", "0" / "-.1" and blanks.
 *
 * Kept separate from `isValidLatLong` on purpose: `geo.parseLatLng` uses that for
 * finding locations, which should not start rejecting values because of a routing rule.
 */

type LatLong = { latitude?: unknown; longitude?: unknown };

/** A plain decimal: "39.8807", "-74.653", "40". Rejects "0.", "-.1", "39.9 N", "-". */
const PLAIN_DECIMAL = /^-?\d+(\.\d+)?$/;

const asText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).trim();

/**
 * Why a location cannot be routed to, in words a user can act on, or null when it can.
 */
export const routeLocationProblem = ({ latitude, longitude }: LatLong): string | null => {
  const lat = asText(latitude);
  const lng = asText(longitude);

  if (!lat || !lng) {
    return 'has no latitude/longitude';
  }

  if (!PLAIN_DECIMAL.test(lat) || !PLAIN_DECIMAL.test(lng)) {
    return `has a latitude/longitude that isn't a number (${lat}, ${lng})`;
  }

  const latNum = Number(lat);
  const lngNum = Number(lng);

  if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) {
    return `has a latitude/longitude out of range (${lat}, ${lng})`;
  }

  // Exactly zero is a placeholder in practice: no project sits on the equator or the
  // prime meridian, and every "0" seen in prod data is paired with junk.
  if (latNum === 0 || lngNum === 0) {
    return `has a placeholder latitude/longitude (${lat}, ${lng}), not a real site`;
  }

  return null;
};

/**
 * The toast text for a failed route request. Raw axios errors arrive here
 * (`mapApi` does not wrap them).
 */
export const ROUTE_UNREACHABLE_MESSAGE =
  "Couldn't build a route for the selected projects. One of them may have a location that can't be reached by road. Check each project's address and latitude/longitude, then try again.";

export const routeFailureMessage = (err: any): string => {
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.message;

  if (!err?.response) {
    return "Couldn't get directions: unable to reach the server. Check your connection and try again.";
  }

  // 4xx carry a reason the user can act on (too many projects, a bad coordinate).
  if (status >= 400 && status < 500 && serverMessage) {
    return `Couldn't get directions: ${serverMessage}`;
  }

  // A 5xx is usually the routing provider failing to reach one of the stops; the server
  // does not pass the provider's reason on, so point the user at the locations instead.
  return ROUTE_UNREACHABLE_MESSAGE;
};
